import React, { useMemo, useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import {
  Box,
  Typography,
  Paper,
  Container,
  Tooltip,
  IconButton,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Divider,
  CircularProgress,
} from '@mui/material';
import { alpha, useTheme } from '@mui/material/styles';
import { portal, portalGradientTextSx } from '../styles/dashboardBrand';
import {
  TrendingUp,
  People,
  MonetizationOn,
  ExpandMore,
  ExpandLess,
  LocalFireDepartment,
  Close,
} from '@mui/icons-material';
import DreamyAppsLogo from './DreamyAppsLogo';
import { API, API_BASE_URL } from '../config/apiConfig';
import {
  readHomeCache,
  shouldForceUserDashboardHomeNetwork,
  mergeDashboardRawFields,
} from '../utils/userDashboardHomeCache';
import {
  parseClaimedRankRewardIdsFromAppRow,
  sumRankDiamondsFromRewardIds,
} from '../utils/dreameyDiamonds';

function getUserIdFromToken() {
  const token = localStorage.getItem('token') || localStorage.getItem('authToken');
  if (!token) return null;
  try {
    const id = JSON.parse(atob(token.split('.')[1])).userId;
    if (id == null) return null;
    const n = Number(id);
    return Number.isFinite(n) ? n : null;
  } catch {
    return null;
  }
}

function isToday(dateStr) {
  if (!dateStr) return false;
  const dt = new Date(dateStr);
  const today = new Date();
  return dt.toDateString() === today.toDateString();
}

/** Display only — must match product rule: 1 Dreamey Coin = 100 Diamonds. */
const DIAMONDS_PER_DREAMEY_COIN = 100;
/** Display only — 1 Ruby = this many Engagement Points (MTD tokens). */
const ENGAGEMENT_POINTS_PER_RUBY = 5;
const DREAMEY_COIN_IMAGE_SRC = `${process.env.PUBLIC_URL || ''}/dreamey-coin-dc.png`;
const RUBY_GEM_IMAGE_SRC = `${process.env.PUBLIC_URL || ''}/dreamey-ruby-gem.png`;

const dreameyCoinFlipKeyframes = {
  '@keyframes dreameyCoinFlipY': {
    '0%': { transform: 'rotateY(0deg)' },
    '100%': { transform: 'rotateY(360deg)' },
  },
};

/** Continuous horizontal (Y-axis) spin for DC artwork — use on img `sx` with a parent `perspective`. */
const dreameyCoinFlipAnimationSx = {
  ...dreameyCoinFlipKeyframes,
  animation: 'dreameyCoinFlipY 2.75s linear infinite',
  transformStyle: 'preserve-3d',
  backfaceVisibility: 'visible',
};

/** Set to true to show the “Current Month Engagement” heading and stat cards again. */
const SHOW_CURRENT_MONTH_ENGAGEMENT = false;

/** EP (engagement points) pill: hidden in UI for now; keep markup below — set to false to show again. */
const HIDE_EP_METRIC_PILL = true;

function formatDreameyCoinFromDiamonds(diamondCount) {
  const dc = (Number(diamondCount) || 0) / DIAMONDS_PER_DREAMEY_COIN;
  return dc.toLocaleString(undefined, { maximumFractionDigits: 2, minimumFractionDigits: 0 });
}

function formatRubyFromEngagementPoints(engagementPoints) {
  const ep = Number(engagementPoints) || 0;
  const rubies = ep / ENGAGEMENT_POINTS_PER_RUBY;
  return rubies.toLocaleString(undefined, { maximumFractionDigits: 2, minimumFractionDigits: 0 });
}

function buildLedgerUrl(userId, fromDate, toDate) {
  let base;
  if (API?.tokenLedger) base = `${API.tokenLedger}/${userId}`;
  else if (API?.tokenLedgerByUser) base = `${API.tokenLedgerByUser}/${userId}`;
  else if (API?.earnings) base = `${API.earnings}/token-ledger/${userId}`;
  else base = `${API_BASE_URL}/earnings/token-ledger/${userId}`;
  if (fromDate && toDate) {
    const sep = base.includes('?') ? '&' : '?';
    return `${base}${sep}from=${encodeURIComponent(fromDate)}&to=${encodeURIComponent(toDate)}`;
  }
  return base;
}

/** My Summary metric pills: full-width equal columns on xs; compact flex from sm up. */
function metricPillShellSx(t) {
  const isDark = t.palette.mode === 'dark';
  return {
    border: '1px solid',
    borderColor: isDark ? alpha(portal.cyan, 0.22) : alpha(t.palette.divider, 0.85),
    borderRadius: '999px',
    px: { xs: 0.65, sm: 1.25 },
    py: { xs: 0.85, sm: 0.6 },
    width: { xs: '100%', sm: 'auto' },
    minWidth: 0,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    background: isDark
      ? `linear-gradient(155deg, ${alpha('#1e2438', 0.95)} 0%, ${alpha('#12151f', 0.98)} 100%)`
      : `linear-gradient(155deg, ${alpha('#fff', 0.98)} 0%, ${alpha('#f4f8ff', 0.95)} 100%)`,
    boxShadow: isDark
      ? `inset 0 1px 0 ${alpha('#fff', 0.06)}, 0 4px 20px ${alpha('#000', 0.25)}`
      : `0 2px 12px ${alpha(portal.cyan, 0.08)}`,
    transition: 'border-color 0.2s ease, box-shadow 0.2s ease, transform 0.2s ease',
    '&:hover': {
      borderColor: alpha(portal.cyan, isDark ? 0.4 : 0.35),
      boxShadow: isDark
        ? `inset 0 1px 0 ${alpha('#fff', 0.08)}, 0 6px 24px ${alpha(portal.cyan, 0.12)}`
        : `0 4px 16px ${alpha(portal.cyan, 0.14)}`,
    },
  };
}

/** Framed panel for My Summary (hideWelcomePromo + streakDiamondMetrics). */
function mySummaryPanelSx(t) {
  const isDark = t.palette.mode === 'dark';
  return {
    position: 'relative',
    borderRadius: 4,
    p: { xs: 1.75, sm: 2.5 },
    mb: { xs: 0.5, sm: 1 },
    overflow: 'hidden',
    border: `1px solid ${alpha(portal.cyan, isDark ? 0.2 : 0.18)}`,
    background: isDark
      ? `linear-gradient(168deg, ${alpha('#171a28', 0.94)} 0%, ${alpha('#0c0e16', 0.98)} 55%, ${alpha('#08090f', 1)} 100%)`
      : `linear-gradient(168deg, ${alpha('#fff', 0.97)} 0%, ${alpha('#f5f9ff', 0.92)} 100%)`,
    boxShadow: isDark
      ? `0 24px 56px ${alpha('#000', 0.42)}, inset 0 1px 0 ${alpha('#fff', 0.05)}`
      : `0 16px 40px ${alpha(portal.indigo, 0.1)}`,
    backdropFilter: 'blur(20px)',
    '&::before': {
      content: '""',
      position: 'absolute',
      top: 0,
      left: 0,
      right: 0,
      height: 4,
      background: portal.gradientBar,
      opacity: isDark ? 0.92 : 0.85,
    },
  };
}

const WelcomeHeader = ({
  user,
  isAdmin = false,
  middleContent = null,
  hideWelcomePromo = false,
  /** My Summary: Home-style streak + diamond pills before “Previous Months Engagement”. */
  streakDiamondMetrics = null,
  /** When false, CC shows … — avoids 0 while earnings summary is still loading (user was {}). */
  earningsSummaryLoaded = true,
}) => {
  const theme = useTheme();
  const [showPreviousMonth, setShowPreviousMonth] = useState(false);
  const [todayTokens, setTodayTokens] = useState(0);
  const [diamondBreakdownOpen, setDiamondBreakdownOpen] = useState(false);
  const [diamondBreakdownLoading, setDiamondBreakdownLoading] = useState(false);
  const [diamondBreakdownError, setDiamondBreakdownError] = useState(null);
  const [diamondBreakdownRows, setDiamondBreakdownRows] = useState([]);
  const [diamondBreakdownTotals, setDiamondBreakdownTotals] = useState({
    rankSum: 0,
    downlineSum: 0,
    baseSum: 0,
    totalSum: 0,
  });

  const userId = useMemo(() => {
    const fromUser = user?.userId ?? user?.UserID ?? user?.id;
    if (fromUser != null && Number.isFinite(Number(fromUser))) return Number(fromUser);
    return getUserIdFromToken();
  }, [user]);

  useEffect(() => {
    if (!userId) {
      setTodayTokens(0);
      return;
    }
    if (!shouldForceUserDashboardHomeNetwork()) {
      const raw = readHomeCache(userId)?.raw;
      if (raw && typeof raw.ledgerTodayTokens === 'number') {
        setTodayTokens(raw.ledgerTodayTokens);
        return;
      }
    }
    let cancelled = false;
    const token = localStorage.getItem('token') || localStorage.getItem('authToken') || '';
    const toDate = new Date();
    const fromDate = new Date();
    fromDate.setDate(fromDate.getDate() - 30);
    const fromStr = fromDate.toISOString().split('T')[0];
    const toStr = toDate.toISOString().split('T')[0];
    const url = buildLedgerUrl(userId, fromStr, toStr);
    fetch(url, {
      headers: {
        'Content-Type': 'application/json',
        ...(token ? { Authorization: `Bearer ${token}` } : {}),
      },
    })
      .then((res) => res.ok ? res.json() : Promise.reject(new Error(res.statusText)))
      .then((data) => {
        if (cancelled) return;
        const items = Array.isArray(data) ? data : (data?.ledger ?? data?.data ?? []);
        const today = Array.isArray(items)
          ? items.filter((tx) => isToday(tx?.CreatedAt ?? tx?.CreatedAtUtc ?? ''))
          : [];
        const sum = today.reduce(
          (acc, tx) => acc + (Number(tx?.TokensGenerated ?? tx?.Tokens ?? 0) || 0),
          0
        );
        setTodayTokens(sum);
        mergeDashboardRawFields(userId, { ledgerTodayTokens: sum });
      })
      .catch(() => {
        if (!cancelled) setTodayTokens(0);
      });
    return () => { cancelled = true; };
  }, [userId]);

  const loadDiamondBreakdown = useCallback(async () => {
    setDiamondBreakdownLoading(true);
    setDiamondBreakdownError(null);
    try {
      const token = localStorage.getItem('token') || localStorage.getItem('authToken') || '';
      const res = await fetch(API.appCoinsApps, {
        headers: token ? { Authorization: `Bearer ${token}` } : {},
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok || !data?.success || !Array.isArray(data.apps)) {
        throw new Error(data?.error || data?.message || 'Could not load app balances.');
      }
      const apps = data.apps;
      const downlineByAppId = {};
      await Promise.all(
        apps.map(async (row) => {
          const appId = Number(row.appId ?? row.AppId);
          if (!Number.isFinite(appId)) return;
          try {
            const dr = await fetch(
              `${API.appCoinsDownlineStreak}?appId=${encodeURIComponent(appId)}`,
              { headers: token ? { Authorization: `Bearer ${token}` } : {} }
            );
            const dj = await dr.json().catch(() => ({}));
            if (dr.ok && dj?.success && Array.isArray(dj.claims)) {
              downlineByAppId[appId] = dj.claims.reduce(
                (s, c) => s + (Number(c.diamondAmount) || 0),
                0
              );
            } else {
              downlineByAppId[appId] = 0;
            }
          } catch {
            downlineByAppId[appId] = 0;
          }
        })
      );
      const rows = apps.map((a) => {
        const appId = Number(a.appId ?? a.AppId);
        const name = String(a.appName ?? a.AppName ?? 'App').trim() || 'App';
        const total = Number(a.dreameyDiamonds ?? a.DreameyDiamonds ?? 0) || 0;
        const claimed = parseClaimedRankRewardIdsFromAppRow(a);
        const rank = sumRankDiamondsFromRewardIds(claimed);
        const downline = Number.isFinite(appId) ? downlineByAppId[appId] ?? 0 : 0;
        const base = Math.max(0, total - rank - downline);
        return { appId, appName: name, total, rank, downline, base };
      });
      setDiamondBreakdownRows(rows);
      const rankSum = rows.reduce((s, r) => s + r.rank, 0);
      const downlineSum = rows.reduce((s, r) => s + r.downline, 0);
      const baseSum = rows.reduce((s, r) => s + r.base, 0);
      setDiamondBreakdownTotals({
        rankSum,
        downlineSum,
        baseSum,
        totalSum: baseSum + rankSum + downlineSum,
      });
    } catch (e) {
      setDiamondBreakdownError(e.message || 'Failed to load breakdown.');
      setDiamondBreakdownRows([]);
      setDiamondBreakdownTotals({ rankSum: 0, downlineSum: 0, baseSum: 0, totalSum: 0 });
    } finally {
      setDiamondBreakdownLoading(false);
    }
  }, []);

  const openDiamondBreakdown = useCallback(() => {
    setDiamondBreakdownOpen(true);
    void loadDiamondBreakdown();
  }, [loadDiamondBreakdown]);

  const diamondBreakdownPerAppVisible = useMemo(
    () => diamondBreakdownRows.filter((r) => Number(r.base) > 0),
    [diamondBreakdownRows]
  );

  const { previousStats, currentStats, currentMonthTokens, availableBalance, referralCount } = useMemo(() => {
    const getNumber = (obj, keys, fallback = 0) => {
      if (!obj) return fallback;
      for (const key of keys) {
        if (obj[key] !== undefined && obj[key] !== null) {
          const parsed = Number(obj[key]);
          if (!Number.isNaN(parsed)) {
            return parsed;
          }
        }
      }
      return fallback;
    };

    const getNullableNumber = (obj, keys) => {
      if (!obj) return null;
      for (const key of keys) {
        if (obj[key] !== undefined && obj[key] !== null) {
          const parsed = Number(obj[key]);
          if (!Number.isNaN(parsed)) {
            return parsed;
          }
        }
      }
      return null;
    };

    const formatCurrency = (value) =>
      new Intl.NumberFormat(undefined, {
        style: 'currency',
        currency: 'USD',
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
      }).format(value);

    const formatNumber = (value) =>
      new Intl.NumberFormat(undefined, {
        maximumFractionDigits: 0,
      }).format(value);

    const formatMinutes = (value) => {
      const minutes = Number(value);
      if (!Number.isFinite(minutes) || minutes <= 0) return '0 min';
      if (minutes < 60) return `${Math.round(minutes)} min`;
      const hours = Math.floor(minutes / 60);
      const remaining = Math.round(minutes % 60);
      if (remaining === 0) return `${hours}h`;
      return `${hours}h ${remaining}m`;
    };

    const formatNullableCurrency = (value) => {
      if (value === null || value === undefined || Number.isNaN(Number(value))) {
        return '—';
      }
      return formatCurrency(Number(value));
    };

    const availableBalance = getNullableNumber(user, [
      'availableBalance',
      'AvailableBalance',
      'walletBalance',
      'WalletBalance',
      'balance',
      'Balance',
    ]);
    const previousPeriodUSD = getNullableNumber(user, ['previousPeriodUSD', 'PreviousPeriodUSD']);
    /* CC = ActiveConnections from process.mlm_GetEarningsSummary (15 generations, placement tree). Prefer that over generic referralCount. */
    const referralCount = getNumber(user, [
      'ActiveConnections',
      'activeConnections',
      'referralCount',
      'Referrals',
    ]);
    const engagementMinutes = getNumber(user, ['engagementMinutes', 'EngagementMinutes', 'videosWatched', 'VideosWatched']);
    const transfers = getNumber(user, [
      'lastTransferAmount',
      'LastTransferAmount',
      'transfers',
      'Transfers',
      'TotalTransfers',
      'withdrawalsUSD',
    ]);
    const currentMonthTokens = getNumber(user, ['currentMonthTokens', 'CurrentMonthTokens']);
    const currentMonthEstimatedUSD = getNullableNumber(user, ['currentMonthEstimatedUSD', 'CurrentMonthEstimatedUSD']);
    const currentMonthMinutes = getNumber(user, ['currentMonthMinutes', 'CurrentMonthMinutes']);
    const currentMonthConnections = getNumber(user, ['currentMonthNewConnections', 'CurrentMonthNewConnections']);

    const previous = [
      {
        icon: <TrendingUp />,
        label: 'Total Engagement Share',
        value: previousPeriodUSD !== null ? formatCurrency(previousPeriodUSD) : formatCurrency(0),
        color: '#4caf50',
        tooltip: 'Your converted earnings from the previous engagement period.',
      },
      {
        icon: <MonetizationOn />,
        label: 'Last Transfer',
        value: formatCurrency(transfers),
        color: portal.purple,
        tooltip: 'Most recent approved transfer processed from your balance.',
      },
    ];

    const current = [
      {
        icon: <People />,
        label: 'New Connections (MTD)',
        value: formatNumber(currentMonthConnections),
        color: portal.cyan,
        tooltip: 'Direct referrals who joined under you this month.',
      },
    ];

    return {
      previousStats: previous,
      currentStats: current,
      currentMonthTokens,
      availableBalance,
      referralCount,
    };
  }, [user]);

  const totalEngagementStats = useMemo(() => {
    if (!streakDiamondMetrics) return [];
    const formatNullableCurrency = (value) => {
      if (value === null || value === undefined || Number.isNaN(Number(value))) {
        return '—';
      }
      return new Intl.NumberFormat(undefined, {
        style: 'currency',
        currency: 'USD',
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
      }).format(Number(value));
    };
    return [
      {
        iconFullBleed: true,
        icon: (
          <Box
            component="img"
            src={DREAMEY_COIN_IMAGE_SRC}
            alt=""
            sx={{
              width: '142%',
              height: '142%',
              objectFit: 'contain',
              objectPosition: 'center',
              display: 'block',
              flexShrink: 0,
            }}
          />
        ),
        label: 'Dreamey Coin',
        value: formatDreameyCoinFromDiamonds(streakDiamondMetrics.diamondCount),
        showCoinAfterValue: true,
        color: '#d97706',
        tooltip: `Dreamey Coin (DC). 1 DC = ${DIAMONDS_PER_DREAMEY_COIN} Diamonds. Shown amount is derived from your Diamond balance.`,
      },
      {
        iconBareImage: true,
        icon: (
          <Box
            component="img"
            src={RUBY_GEM_IMAGE_SRC}
            alt=""
            sx={{
              width: '100%',
              height: '100%',
              maxWidth: '100%',
              objectFit: 'contain',
              objectPosition: 'center',
              display: 'block',
            }}
          />
        ),
        label: 'Ruby',
        value: formatRubyFromEngagementPoints(currentMonthTokens),
        showRubyIconAfterValue: true,
        color: '#e11d48',
        tooltip: `Ruby amount derived from Engagement Points (MTD). 1 Ruby = ${ENGAGEMENT_POINTS_PER_RUBY} Engagement Points.`,
      },
      {
        icon: <MonetizationOn />,
        label: 'Available Balance',
        value: formatNullableCurrency(availableBalance),
        showBalanceIconAfterValue: true,
        color: '#22c55e',
        tooltip: 'Current withdrawable balance, synced with your Redemptions page.',
      },
    ];
  }, [streakDiamondMetrics, currentMonthTokens, availableBalance]);

  const currentMonthStatsRow = useMemo(() => {
    if (!streakDiamondMetrics) return currentStats;
    return [
      ...currentStats,
      {
        icon: <LocalFireDepartment />,
        label: 'Streak',
        value: streakDiamondMetrics.loading
          ? '…'
          : `${Number(streakDiamondMetrics.streakDays ?? 0).toLocaleString()} 🔥`,
        color: '#ff9800',
        tooltip: 'Your current consecutive-day activity streak (same data as Home).',
      },
    ];
  }, [currentStats, streakDiamondMetrics]);

  return (
    <Box sx={{ mb: { xs: 1, sm: 2 } }}>
      {/* Hero Section */}
      {!hideWelcomePromo && (
      <Paper
        elevation={0}
        sx={{
          background: portal.gradientHero,
          color: 'white',
          borderRadius: 3,
          border: '1px solid',
          borderColor: alpha('#fff', 0.25),
          p: { xs: 1.5, sm: 2 },
          mb: { xs: 1.5, sm: 2 },
          position: 'relative',
          overflow: 'hidden',
          boxShadow: `0 12px 40px ${alpha(portal.cyan, 0.35)}`,
          '&::before': {
            content: '""',
            position: 'absolute',
            top: 0,
            right: 0,
            width: { xs: '100px', sm: '150px' },
            height: { xs: '100px', sm: '150px' },
            background: 'radial-gradient(circle, rgba(255,255,255,0.1) 0%, transparent 70%)',
            borderRadius: '50%',
            transform: 'translate(50%, -50%)',
          },
          '&::after': {
            content: '""',
            position: 'absolute',
            bottom: 0,
            left: 0,
            width: { xs: '70px', sm: '100px' },
            height: { xs: '70px', sm: '100px' },
            background: 'radial-gradient(circle, rgba(255,255,255,0.05) 0%, transparent 70%)',
            borderRadius: '50%',
            transform: 'translate(-50%, 50%)',
          },
        }}
      >
        <Container maxWidth="lg">
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', position: 'relative', zIndex: 1, flexDirection: { xs: 'column', md: 'row' }, textAlign: { xs: 'center', md: 'left' } }}>
            <Box>
              <Typography variant="h4" component="h1" sx={{ fontWeight: 700, mb: { xs: 0.25, sm: 0.5 }, fontSize: { xs: '1rem', sm: '1.5rem', md: '1.75rem' } }}>
                Welcome To Dreamey Vids
              </Typography>
              <Typography variant="subtitle1" sx={{ opacity: 0.9, mb: { xs: 0.5, sm: 1 }, fontSize: { xs: '0.75rem', sm: '1rem' } }}>
                {isAdmin ? 'Admin Dashboard' : 'Your Creative Journey Starts Here'}
              </Typography>
              <Typography variant="body2" sx={{ opacity: 0.8, maxWidth: '600px', fontSize: { xs: '0.65rem', sm: '0.875rem' } }}>
                {isAdmin 
                  ? 'Manage users, content, and platform operations with powerful admin tools.'
                  : 'Create amazing content, earn rewards, and build your community. Turn your creativity into income with our innovative platform.'
                }
              </Typography>
            </Box>
            <Box sx={{ display: { xs: 'none', md: 'block' } }}>
              <DreamyAppsLogo size="large" showText={false} variant="colorful" />
            </Box>
          </Box>
        </Container>
      </Paper>
      )}

      {/* Middle Content (e.g., alerts, notices) - moved above download link */}
      {middleContent && (
        <Container maxWidth="xl" sx={{ px: { xs: 2, sm: 3, md: 4 }, mb: 2 }}>
          {middleContent}
        </Container>
      )}

      {/* App Download Link - redirects to My Apps tab */}
      {!hideWelcomePromo && (
      <Container maxWidth="xl" sx={{ px: { xs: 2, sm: 3, md: 4 }, mb: 3, display: 'flex', justifyContent: 'center' }}>
        <Paper
          component={Link}
          to="/dashboard/apps"
          elevation={0}
          sx={(theme) => ({
            display: 'inline-block',
            width: 'fit-content',
            textDecoration: 'none',
            p: { xs: 2, sm: 3 },
            borderRadius: 2,
            background:
              theme.palette.mode === 'dark'
                ? 'rgba(255,255,255,0.06)'
                : 'rgba(255,255,255,0.8)',
            backdropFilter: 'blur(10px)',
            border:
              theme.palette.mode === 'dark'
                ? '1px solid rgba(255,255,255,0.08)'
                : '1px solid rgba(255,255,255,0.2)',
            transition: 'background 0.2s, border-color 0.2s',
            '&:hover': {
              background: theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.1)' : 'rgba(255,255,255,0.95)',
              borderColor: theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.15)' : 'rgba(255,255,255,0.4)',
            },
            '@keyframes popInOut': {
              '0%, 200%': { transform: 'scale(1)', opacity: 1.1 },
              '50%': { transform: 'scale(1.08)', opacity: 0.8 },
            },
            animation: 'popInOut 1.5s ease-in-out infinite',
          })}
        >
          <Typography
            variant="h6"
            sx={{
              fontWeight: 600,
              color: '#22c55e',
              fontSize: { xs: '0.875rem', sm: '1.125rem' },
              textAlign: 'center',
            }}
          >
            Click here to download our Dreamey Vids App
          </Typography>
        </Paper>
      </Container>
      )}

      {/* Tokens — Today's current tokens: 0 red, 1-5 yellow, 5-8 blue, 9-10 green */}
      {!hideWelcomePromo &&
      (() => {
        const tokenColor = todayTokens === 0 ? '#dc2626' : todayTokens <= 4 ? '#eab308' : todayTokens <= 8 ? '#2563eb' : '#22c55e';
        return (
          <Container maxWidth="xl" sx={{ px: { xs: 2, sm: 3, md: 4 }, mb: 3, display: 'flex', justifyContent: 'center' }}>
            <Paper
              elevation={0}
              sx={(theme) => ({
                display: 'inline-block',
                width: 'fit-content',
                p: { xs: 1.5, sm: 2 },
                borderRadius: 2,
                background:
                  theme.palette.mode === 'dark'
                    ? 'rgba(255,255,255,0.06)'
                    : 'rgba(255,255,255,0.8)',
                backdropFilter: 'blur(10px)',
                border:
                  theme.palette.mode === 'dark'
                    ? '1px solid rgba(255,255,255,0.08)'
                    : '1px solid rgba(255,255,255,0.2)',
              })}
            >
              <Typography variant="body1" sx={{ fontWeight: 600, color: tokenColor, fontSize: { xs: '0.875rem', sm: '1rem' } }}>
                {Math.min(100, Math.round((todayTokens / 10) * 100))}% completed
              </Typography>
            </Paper>
          </Container>
        );
      })()}

      {/* Stats Cards */}
      <Container maxWidth="xl" sx={{ px: { xs: 2, sm: 3, md: 4 } }}>
        <Box
          sx={(theme) =>
            hideWelcomePromo && streakDiamondMetrics ? mySummaryPanelSx(theme) : {}
          }
        >
        {streakDiamondMetrics ? (
          <Box
            sx={{
              display: { xs: 'grid', sm: 'flex' },
              gridTemplateColumns: {
                xs: HIDE_EP_METRIC_PILL ? 'repeat(3, minmax(0, 1fr))' : 'repeat(4, minmax(0, 1fr))',
              },
              width: '100%',
              alignItems: 'stretch',
              gap: { xs: 0.5, sm: 0.75 },
              mb: 2,
              flexWrap: { sm: 'wrap' },
            }}
          >
            <Box
              sx={(t) => ({
                ...metricPillShellSx(t),
                ...(HIDE_EP_METRIC_PILL ? { display: 'none' } : {}),
              })}
            >
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 0.5,
                  justifyContent: 'center',
                  width: '100%',
                  minWidth: 0,
                }}
              >
                <Typography
                  variant="caption"
                  color="text.secondary"
                  sx={{ fontSize: { xs: '0.7rem', sm: '0.75rem' }, flexShrink: 0 }}
                >
                  EP :
                </Typography>
                <Typography
                  variant="body2"
                  sx={{
                    fontWeight: 800,
                    color: 'common.white',
                    fontSize: { xs: '0.9375rem', sm: '0.875rem' },
                    minWidth: 0,
                  }}
                >
                  {(Number(currentMonthTokens) || 0).toLocaleString(undefined, { maximumFractionDigits: 0 })}
                </Typography>
              </Box>
            </Box>
            <Box
              component="button"
              type="button"
              onClick={openDiamondBreakdown}
              onKeyDown={(e) => {
                if (e.key === 'Enter' || e.key === ' ') {
                  e.preventDefault();
                  openDiamondBreakdown();
                }
              }}
              aria-haspopup="dialog"
              aria-label="Open diamond balance breakdown by app, rank, and downline"
              sx={(t) => ({
                ...metricPillShellSx(t),
                cursor: 'pointer',
                border: 'none',
                font: 'inherit',
                color: 'inherit',
                width: { xs: '100%', sm: 'auto' },
              })}
            >
              <Typography
                variant="body2"
                sx={{
                  fontWeight: 800,
                  color: 'common.white',
                  fontSize: { xs: '0.9375rem', sm: '0.875rem' },
                  textAlign: 'center',
                }}
              >
                {`${Number(streakDiamondMetrics.diamondCount ?? 0).toLocaleString()} 💎`}
              </Typography>
            </Box>
            <Box sx={(t) => metricPillShellSx(t)}>
              <Typography
                variant="body2"
                sx={{
                  fontWeight: 800,
                  color: 'common.white',
                  fontSize: { xs: '0.9375rem', sm: '0.875rem' },
                  textAlign: 'center',
                }}
              >
                {streakDiamondMetrics.loading
                  ? '…'
                  : `${Number(streakDiamondMetrics.streakDays ?? 0).toLocaleString()} 🔥`}
              </Typography>
            </Box>
            <Tooltip
              title="Community connections (ActiveConnections): everyone in your placement downline within 15 generations (same depth cap as rank). Uses normalized referral codes so the count matches the database. Direct signups follow the same rules as My Referrals."
              placement="top"
              enterTouchDelay={0}
            >
              <Box sx={(t) => metricPillShellSx(t)} component="span" display="block">
                <Box
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 0.5,
                    justifyContent: 'center',
                    width: '100%',
                    minWidth: 0,
                  }}
                >
                  <Typography
                    variant="caption"
                    color="text.secondary"
                    sx={{ fontSize: { xs: '0.7rem', sm: '0.75rem' }, flexShrink: 0 }}
                  >
                    CC :
                  </Typography>
                  <Typography
                    variant="body2"
                    sx={{
                      fontWeight: 800,
                      color: 'common.white',
                      fontSize: { xs: '0.9375rem', sm: '0.875rem' },
                      minWidth: 0,
                    }}
                  >
                    {earningsSummaryLoaded === false
                      ? '…'
                      : Number(referralCount ?? 0).toLocaleString(undefined, { maximumFractionDigits: 0 })}
                  </Typography>
                </Box>
              </Box>
            </Tooltip>
          </Box>
        ) : null}
        {totalEngagementStats.length > 0 && (
          <>
            <Typography
              variant="h5"
              sx={portalGradientTextSx({
                fontWeight: 800,
                letterSpacing: '-0.01em',
                mb: 2,
                fontSize: { xs: '1rem', sm: '1.55rem' },
              })}
            >
              Total Engagement
            </Typography>
            <Box
              sx={{
                display: 'grid',
                gridTemplateColumns: {
                  xs: '1fr',
                  sm: 'repeat(2, 1fr)',
                  md: 'repeat(3, 1fr)',
                },
                gap: { xs: 1.25, sm: 2 },
                mb: 2.5,
                width: '100%',
                overflow: 'visible',
              }}
            >
              {totalEngagementStats.map((stat, index) => (
                <Tooltip key={`total-engagement-${index}`} title={stat.tooltip} placement="top" enterTouchDelay={0}>
                  <Paper
                    elevation={0}
                    sx={(t) => ({
                      position: 'relative',
                      p: { xs: 1.5, sm: 2.25 },
                      pt: { xs: 1.75, sm: 2.5 },
                      borderRadius: 3,
                      background: t.palette.mode === 'dark'
                        ? `linear-gradient(165deg, ${alpha('#12182a', 0.94)} 0%, ${alpha('#0b1222', 0.9)} 45%, ${alpha('#080c14', 0.95)} 100%)`
                        : `linear-gradient(165deg, ${alpha('#ffffff', 0.98)} 0%, ${alpha('#f5f9ff', 0.92)} 100%)`,
                      backdropFilter: 'blur(14px)',
                      border: `1px solid ${alpha(t.palette.mode === 'dark' ? portal.cyan : t.palette.common.white, t.palette.mode === 'dark' ? 0.16 : 0.35)}`,
                      boxShadow: t.palette.mode === 'dark'
                        ? `inset 0 1px 0 ${alpha('#fff', 0.07)}, 0 14px 36px ${alpha('#000', 0.4)}, 0 0 0 1px ${alpha('#fff', 0.03)} inset`
                        : `inset 0 1px 0 ${alpha('#fff', 0.95)}, 0 12px 28px ${alpha(portal.cyan, 0.1)}`,
                      transition: 'transform 0.25s ease, box-shadow 0.25s ease, border-color 0.25s ease',
                      width: '100%',
                      minWidth: 0,
                      overflow: 'hidden',
                      '&::before': {
                        content: '""',
                        position: 'absolute',
                        top: 0,
                        left: 0,
                        right: 0,
                        height: 3,
                        background: portal.gradientBar,
                        opacity: t.palette.mode === 'dark' ? 0.55 : 0.4,
                        pointerEvents: 'none',
                      },
                      '&:hover': {
                        transform: 'translateY(-4px)',
                        borderColor: alpha(portal.cyan, 0.45),
                        boxShadow: t.palette.mode === 'dark'
                          ? `inset 0 1px 0 ${alpha('#fff', 0.08)}, 0 18px 44px ${alpha('#000', 0.48)}, 0 0 28px ${alpha(portal.cyan, 0.08)}`
                          : `0 16px 36px ${alpha(portal.indigo, 0.14)}`,
                      },
                      cursor: 'default',
                    })}
                  >
                    <Box sx={{ display: 'flex', flexDirection: { xs: 'row', sm: 'column' }, alignItems: { xs: 'center', sm: 'flex-start' }, justifyContent: { xs: 'space-between', sm: 'flex-start' }, width: '100%', height: '100%', position: 'relative', zIndex: 1 }}>
                      <Box sx={{ display: 'flex', flexDirection: { xs: 'row', sm: 'column' }, alignItems: { xs: 'center', sm: 'flex-start' }, mb: { xs: 0, sm: 2 }, flex: { xs: 1, sm: 'none' }, minWidth: 0, width: { xs: 'auto', sm: '100%' } }}>
                        <Box
                          sx={(t) =>
                            stat.iconBareImage
                              ? {
                                  p: 0,
                                  width: { xs: 44, sm: 50 },
                                  height: { xs: 40, sm: 46 },
                                  overflow: 'visible',
                                  mr: { xs: 1, sm: 0 },
                                  mb: { xs: 0, sm: 1.5 },
                                  display: 'flex',
                                  alignItems: 'center',
                                  justifyContent: 'center',
                                  flexShrink: 0,
                                  border: 'none',
                                  background: 'none',
                                  borderRadius: 0,
                                  '& img': { display: 'block' },
                                }
                              : {
                                  ...(stat.iconFullBleed
                                    ? {
                                        p: 0,
                                        width: { xs: 40, sm: 46 },
                                        height: { xs: 40, sm: 46 },
                                        overflow: 'hidden',
                                      }
                                    : {
                                        p: { xs: 0.8, sm: 1.1 },
                                      }),
                                  borderRadius: '999px',
                                  background: t.palette.mode === 'dark'
                                    ? `linear-gradient(135deg, ${alpha(stat.color, 0.32)}, ${alpha(portal.cyan, 0.16)})`
                                    : `linear-gradient(135deg, ${alpha(stat.color, 0.22)}, ${alpha(portal.cyan, 0.1)})`,
                                  color: stat.color,
                                  mr: { xs: 1, sm: 0 },
                                  mb: { xs: 0, sm: 1.5 },
                                  display: 'flex',
                                  alignItems: 'center',
                                  justifyContent: 'center',
                                  flexShrink: 0,
                                  border: `1px solid ${alpha(stat.color, t.palette.mode === 'dark' ? 0.35 : 0.25)}`,
                                  '& svg': { fontSize: { xs: '18px', sm: '21px' } },
                                  ...(!stat.iconFullBleed ? { '& img': { display: 'block' } } : {}),
                                }
                          }
                        >
                          {stat.icon}
                        </Box>
                        <Typography
                          variant="body2"
                          sx={(t) => ({
                            fontWeight: 600,
                            letterSpacing: 0.15,
                            fontSize: { xs: '0.78rem', sm: '0.93rem' },
                            lineHeight: 1.2,
                            color: t.palette.mode === 'dark' ? alpha('#fff', 0.9) : t.palette.text.secondary,
                            whiteSpace: { xs: 'nowrap', sm: 'normal' },
                            overflow: { xs: 'hidden', sm: 'visible' },
                            textOverflow: { xs: 'ellipsis', sm: 'clip' },
                            flex: { xs: 1, sm: 'none' },
                            minWidth: 0,
                            width: { xs: 'auto', sm: '100%' },
                          })}
                        >
                          {stat.label}
                        </Typography>
                      </Box>
                      <Box sx={{ flexShrink: 0, ml: { xs: 1, sm: 0 }, mt: { xs: 0, sm: 'auto' }, minWidth: 0 }}>
                        <Box
                          sx={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: { xs: 0.5, sm: 0.75 },
                            flexWrap: 'nowrap',
                          }}
                        >
                          <Typography
                            variant="h4"
                            component="span"
                            sx={portalGradientTextSx({
                              fontWeight: 800,
                              letterSpacing: '-0.01em',
                              fontSize: { xs: '1.65rem', sm: '2rem' },
                            })}
                          >
                            {stat.value}
                          </Typography>
                          {stat.showCoinAfterValue ? (
                            <Box
                              sx={{
                                perspective: '120px',
                                flexShrink: 0,
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                              }}
                            >
                              <Box
                                component="img"
                                src={DREAMEY_COIN_IMAGE_SRC}
                                alt=""
                                sx={{
                                  width: { xs: 28, sm: 32 },
                                  height: { xs: 28, sm: 32 },
                                  objectFit: 'contain',
                                  display: 'block',
                                  ...dreameyCoinFlipAnimationSx,
                                }}
                              />
                            </Box>
                          ) : null}
                          {stat.showRubyIconAfterValue ? (
                            <Box
                              component="img"
                              src={RUBY_GEM_IMAGE_SRC}
                              alt=""
                              sx={{
                                width: { xs: 24, sm: 26 },
                                height: { xs: 28, sm: 32 },
                                objectFit: 'contain',
                                objectPosition: 'center',
                                flexShrink: 0,
                                display: 'block',
                              }}
                            />
                          ) : null}
                          {stat.showBalanceIconAfterValue ? (
                            <Box
                              sx={(t) => ({
                                width: { xs: 28, sm: 32 },
                                height: { xs: 28, sm: 32 },
                                borderRadius: '999px',
                                flexShrink: 0,
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                background:
                                  t.palette.mode === 'dark'
                                    ? `linear-gradient(135deg, ${alpha(stat.color, 0.32)}, ${alpha(portal.cyan, 0.16)})`
                                    : `linear-gradient(135deg, ${alpha(stat.color, 0.22)}, ${alpha(portal.cyan, 0.1)})`,
                                border: `1px solid ${alpha(stat.color, t.palette.mode === 'dark' ? 0.35 : 0.25)}`,
                                color: stat.color,
                                '& svg': { fontSize: { xs: '16px', sm: '18px' } },
                              })}
                            >
                              <MonetizationOn />
                            </Box>
                          ) : null}
                        </Box>
                        {stat.subtitle ? (
                          <Typography
                            variant="caption"
                            sx={{
                              display: 'block',
                              color: 'text.secondary',
                              mt: 0.75,
                              lineHeight: 1.35,
                              fontSize: '0.75rem',
                            }}
                          >
                            {stat.subtitle}
                          </Typography>
                        ) : null}
                      </Box>
                    </Box>
                  </Paper>
                </Tooltip>
              ))}
            </Box>
          </>
        )}
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'flex-start',
            flexWrap: 'wrap',
            gap: { xs: 0.5, sm: 0.75 },
            mb: 1.75,
            p: { xs: 1, sm: 1.15 },
            borderRadius: 2.5,
            background: (theme) =>
              theme.palette.mode === 'dark'
                ? `linear-gradient(135deg, ${alpha('#1a1f2e', 0.5)} 0%, ${alpha('#12151f', 0.35)} 100%)`
                : alpha(portal.cyan, 0.06),
            border: (theme) =>
              `1px solid ${alpha(theme.palette.mode === 'dark' ? portal.cyan : portal.indigo, theme.palette.mode === 'dark' ? 0.14 : 0.2)}`,
            boxShadow: (theme) =>
              theme.palette.mode === 'dark'
                ? `inset 0 1px 0 ${alpha('#fff', 0.04)}`
                : 'none',
          }}
        >
          <Typography
            variant="h5"
            component="span"
            sx={portalGradientTextSx({
              fontWeight: 800,
              letterSpacing: '-0.01em',
              fontSize: { xs: '1rem', sm: '1.55rem' },
            })}
          >
            Previous Months Engagement
          </Typography>
          <IconButton
            size="small"
            onClick={() => setShowPreviousMonth(!showPreviousMonth)}
            sx={(theme) => ({
              color: theme.palette.text.secondary,
              border: '1px solid',
              borderColor: alpha(portal.cyan, theme.palette.mode === 'dark' ? 0.28 : 0.35),
              backgroundColor: alpha(theme.palette.common.white, theme.palette.mode === 'dark' ? 0.06 : 0.85),
              transition: 'border-color 0.2s, box-shadow 0.2s, background-color 0.2s',
              '&:hover': {
                backgroundColor: alpha(theme.palette.common.white, theme.palette.mode === 'dark' ? 0.1 : 0.95),
                borderColor: alpha(portal.cyan, 0.55),
                boxShadow: `0 0 16px ${alpha(portal.cyan, 0.2)}`,
              },
            })}
            aria-label={showPreviousMonth ? 'Hide previous month engagement' : 'Show previous month engagement'}
          >
            {showPreviousMonth ? <ExpandLess /> : <ExpandMore />}
          </IconButton>
        </Box>
        {showPreviousMonth && (
          <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, 1fr)' }, gap: { xs: 1.25, sm: 2 }, mb: 2.5, width: '100%', overflow: 'visible' }}>
            {previousStats.map((stat, index) => (
              <Tooltip key={index} title={stat.tooltip} placement="top" enterTouchDelay={0}>
                <Paper
                  elevation={0}
                  sx={(theme) => ({
                    p: { xs: 1.5, sm: 2.25 },
                    borderRadius: 3,
                    background: theme.palette.mode === 'dark'
                      ? `linear-gradient(160deg, ${alpha('#0b1222', 0.88)} 0%, ${alpha('#0f172a', 0.78)} 100%)`
                      : `linear-gradient(160deg, ${alpha('#ffffff', 0.95)} 0%, ${alpha('#f8fbff', 0.9)} 100%)`,
                    backdropFilter: 'blur(12px)',
                    border: `1px solid ${alpha(theme.palette.common.white, theme.palette.mode === 'dark' ? 0.14 : 0.35)}`,
                    boxShadow: theme.palette.mode === 'dark'
                      ? `inset 0 1px 0 ${alpha('#fff', 0.06)}, 0 12px 32px ${alpha('#000', 0.38)}`
                      : `inset 0 1px 0 ${alpha('#fff', 0.9)}, 0 10px 24px ${alpha('#0f172a', 0.12)}`,
                    transition: 'transform 0.25s ease, box-shadow 0.25s ease, border-color 0.25s ease',
                    width: '100%',
                    minWidth: 0,
                    overflow: 'visible',
                    '&:hover': {
                      transform: 'translateY(-3px)',
                      borderColor: alpha(portal.cyan, 0.42),
                      boxShadow: theme.palette.mode === 'dark'
                        ? `0 14px 36px ${alpha('#000', 0.42)}`
                        : `0 12px 28px ${alpha('#0f172a', 0.16)}`,
                    },
                    cursor: 'default',
                  })}
                >
                  <Box sx={{ display: 'flex', flexDirection: { xs: 'row', sm: 'column' }, alignItems: { xs: 'center', sm: 'flex-start' }, justifyContent: { xs: 'space-between', sm: 'flex-start' }, width: '100%', height: '100%' }}>
                    {/* Icon and Label Container */}
                    <Box sx={{ display: 'flex', flexDirection: { xs: 'row', sm: 'column' }, alignItems: { xs: 'center', sm: 'flex-start' }, mb: { xs: 0, sm: 2 }, flex: { xs: 1, sm: 'none' }, minWidth: 0, width: { xs: 'auto', sm: '100%' } }}>
                      <Box
                        sx={(theme) => ({
                          p: { xs: 0.8, sm: 1.1 },
                          borderRadius: '999px',
                          background: theme.palette.mode === 'dark'
                            ? `linear-gradient(135deg, ${alpha(stat.color, 0.32)}, ${alpha(portal.cyan, 0.16)})`
                            : `linear-gradient(135deg, ${alpha(stat.color, 0.22)}, ${alpha(portal.cyan, 0.1)})`,
                          color: stat.color,
                          mr: { xs: 1, sm: 0 },
                          mb: { xs: 0, sm: 1.5 },
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          flexShrink: 0,
                          border: `1px solid ${alpha(stat.color, theme.palette.mode === 'dark' ? 0.35 : 0.25)}`,
                          '& svg': {
                            fontSize: { xs: '18px', sm: '21px' }
                          }
                        })}
                      >
                        {stat.icon}
                      </Box>
                      <Typography
                        variant="body2"
                        sx={(theme) => ({
                          fontWeight: 600,
                          letterSpacing: 0.15,
                          fontSize: { xs: '0.78rem', sm: '0.93rem' },
                          lineHeight: 1.2,
                          color:
                            theme.palette.mode === 'dark'
                              ? alpha('#fff', 0.9)
                              : theme.palette.text.secondary,
                          whiteSpace: { xs: 'nowrap', sm: 'normal' },
                          overflow: { xs: 'hidden', sm: 'visible' },
                          textOverflow: { xs: 'ellipsis', sm: 'clip' },
                          flex: { xs: 1, sm: 'none' },
                          minWidth: 0,
                          width: { xs: 'auto', sm: '100%' },
                          textAlign: { xs: 'left', sm: 'left' },
                        })}
                      >
                        {stat.label}
                      </Typography>
                    </Box>
                    {/* Value */}
                    <Typography
                      variant="h4"
                      sx={portalGradientTextSx({
                        fontWeight: 800,
                        letterSpacing: '-0.01em',
                        fontSize: { xs: '1.65rem', sm: '2rem' },
                        flexShrink: 0,
                        ml: { xs: 1, sm: 0 },
                        mt: { xs: 0, sm: 'auto' },
                      })}
                    >
                      {stat.value}
                    </Typography>
                  </Box>
                </Paper>
              </Tooltip>
            ))}
          </Box>
        )}

        {SHOW_CURRENT_MONTH_ENGAGEMENT ? (
          <>
        <Typography
          variant="h5"
          sx={portalGradientTextSx({
                fontWeight: 800,
                letterSpacing: '-0.01em',
            mt: { xs: 1, sm: 4 },
            mb: 2,
                fontSize: { xs: '1rem', sm: '1.55rem' },
          })}
        >
          Current Month Engagement
        </Typography>
            <Box
              sx={{
                display: 'grid',
                gridTemplateColumns: {
                  xs: '1fr',
                  sm: 'repeat(2, 1fr)',
                  md: streakDiamondMetrics ? 'repeat(2, 1fr)' : 'repeat(3, 1fr)',
                },
                gap: { xs: 1.25, sm: 2 },
                width: '100%',
                overflow: 'visible',
              }}
            >
              {currentMonthStatsRow.map((stat, index) => (
                <Tooltip key={`current-${stat.label}-${index}`} title={stat.tooltip} placement="top" enterTouchDelay={0}>
              <Paper
                elevation={0}
                sx={(theme) => ({
                      p: { xs: 1.5, sm: 2.25 },
                      borderRadius: 3,
                      background: theme.palette.mode === 'dark'
                        ? `linear-gradient(160deg, ${alpha('#0b1222', 0.88)} 0%, ${alpha('#0f172a', 0.78)} 100%)`
                        : `linear-gradient(160deg, ${alpha('#ffffff', 0.95)} 0%, ${alpha('#f8fbff', 0.9)} 100%)`,
                      backdropFilter: 'blur(12px)',
                      border: `1px solid ${alpha(theme.palette.common.white, theme.palette.mode === 'dark' ? 0.14 : 0.35)}`,
                      boxShadow: theme.palette.mode === 'dark'
                        ? `inset 0 1px 0 ${alpha('#fff', 0.06)}, 0 12px 32px ${alpha('#000', 0.38)}`
                        : `inset 0 1px 0 ${alpha('#fff', 0.9)}, 0 10px 24px ${alpha('#0f172a', 0.12)}`,
                      transition: 'transform 0.25s ease, box-shadow 0.25s ease, border-color 0.25s ease',
                  width: '100%',
                  minWidth: 0,
                  overflow: 'visible',
                  '&:hover': {
                        transform: 'translateY(-3px)',
                        borderColor: alpha(portal.cyan, 0.42),
                        boxShadow: theme.palette.mode === 'dark'
                          ? `0 14px 36px ${alpha('#000', 0.42)}`
                          : `0 12px 28px ${alpha('#0f172a', 0.16)}`,
                  },
                  cursor: 'default',
                })}
              >
                <Box sx={{ display: 'flex', flexDirection: { xs: 'row', sm: 'column' }, alignItems: { xs: 'center', sm: 'flex-start' }, justifyContent: { xs: 'space-between', sm: 'flex-start' }, width: '100%', height: '100%' }}>
                  {/* Icon and Label Container */}
                  <Box sx={{ display: 'flex', flexDirection: { xs: 'row', sm: 'column' }, alignItems: { xs: 'center', sm: 'flex-start' }, mb: { xs: 0, sm: 2 }, flex: { xs: 1, sm: 'none' }, minWidth: 0, width: { xs: 'auto', sm: '100%' } }}>
                    <Box
                      sx={(theme) => ({
                            p: { xs: 0.8, sm: 1.1 },
                            borderRadius: '999px',
                            background: theme.palette.mode === 'dark'
                              ? `linear-gradient(135deg, ${alpha(stat.color, 0.32)}, ${alpha(portal.cyan, 0.16)})`
                              : `linear-gradient(135deg, ${alpha(stat.color, 0.22)}, ${alpha(portal.cyan, 0.1)})`,
                        color: stat.color,
                        mr: { xs: 1, sm: 0 },
                        mb: { xs: 0, sm: 1.5 },
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        flexShrink: 0,
                            border: `1px solid ${alpha(stat.color, theme.palette.mode === 'dark' ? 0.35 : 0.25)}`,
                        '& svg': {
                              fontSize: { xs: '18px', sm: '21px' }
                        }
                      })}
                    >
                      {stat.icon}
                    </Box>
                    <Typography
                      variant="body2"
                      sx={(theme) => ({
                        fontWeight: 600,
                            letterSpacing: 0.15,
                            fontSize: { xs: '0.78rem', sm: '0.93rem' },
                        lineHeight: 1.2,
                        color:
                          theme.palette.mode === 'dark'
                                ? alpha('#fff', 0.9)
                            : theme.palette.text.secondary,
                        whiteSpace: { xs: 'nowrap', sm: 'normal' },
                        overflow: { xs: 'hidden', sm: 'visible' },
                        textOverflow: { xs: 'ellipsis', sm: 'clip' },
                        flex: { xs: 1, sm: 'none' },
                        minWidth: 0,
                        width: { xs: 'auto', sm: '100%' },
                        textAlign: { xs: 'left', sm: 'left' },
                      })}
                    >
                      {stat.label}
                    </Typography>
                  </Box>
                  {/* Value */}
                  <Typography
                    variant="h4"
                    sx={portalGradientTextSx({
                          fontWeight: 800,
                          letterSpacing: '-0.01em',
                          fontSize: { xs: '1.65rem', sm: '2rem' },
                      flexShrink: 0,
                      ml: { xs: 1, sm: 0 },
                      mt: { xs: 0, sm: 'auto' },
                    })}
                  >
                    {stat.value}
                  </Typography>
                </Box>
              </Paper>
            </Tooltip>
          ))}
        </Box>
          </>
        ) : null}
        </Box>
      </Container>

      <Dialog
        open={diamondBreakdownOpen}
        onClose={() => setDiamondBreakdownOpen(false)}
        fullWidth
        maxWidth="sm"
        aria-labelledby="diamond-breakdown-title"
      >
        <DialogTitle
          id="diamond-breakdown-title"
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            gap: 1,
            pr: 1,
          }}
        >
          <Typography component="span" variant="h6" sx={{ fontWeight: 800 }}>
            Diamond balance
          </Typography>
          <IconButton
            type="button"
            aria-label="Close"
            onClick={() => setDiamondBreakdownOpen(false)}
            size="small"
          >
            <Close />
          </IconButton>
        </DialogTitle>
        <DialogContent dividers sx={{ pt: 2 }}>
          {diamondBreakdownLoading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
              <CircularProgress size={36} />
            </Box>
          ) : diamondBreakdownError ? (
            <Typography color="error" variant="body2">
              {diamondBreakdownError}
            </Typography>
          ) : (
            <>
              <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 1, fontWeight: 700 }}>
                Per app (base / sync 💎)
              </Typography>
              {diamondBreakdownRows.length === 0 ? (
                <Typography variant="body2" color="text.secondary">
                  No app balances found.
                </Typography>
              ) : diamondBreakdownPerAppVisible.length === 0 ? (
                <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                  No per-app base/sync balance (all apps are 0).
                </Typography>
              ) : (
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1, mb: 2 }}>
                  {diamondBreakdownPerAppVisible.map((row) => (
                    <Box
                      key={row.appId ?? row.appName}
                      sx={{
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'center',
                        gap: 2,
                        py: 0.75,
                        px: 1,
                        borderRadius: 1,
                        bgcolor: (t) =>
                          t.palette.mode === 'dark' ? alpha('#fff', 0.04) : alpha(portal.cyan, 0.06),
                      }}
                    >
                      <Typography variant="body2" sx={{ fontWeight: 600, minWidth: 0 }}>
                        {row.appName}
                      </Typography>
                      <Typography variant="body2" sx={{ fontWeight: 800, flexShrink: 0 }}>
                        {Number(row.base).toLocaleString()} 💎
                      </Typography>
                    </Box>
                  ))}
                </Box>
              )}
              <Divider sx={{ my: 2 }} />
              <Box
                sx={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                  gap: 2,
                  py: 0.75,
                }}
              >
                <Typography variant="body2" sx={{ fontWeight: 700 }}>
                  Rank rewards (claimed)
                </Typography>
                <Typography variant="body2" sx={{ fontWeight: 800 }}>
                  {Number(diamondBreakdownTotals.rankSum).toLocaleString()} 💎
                </Typography>
              </Box>
              <Box
                sx={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                  gap: 2,
                  py: 0.75,
                }}
              >
                <Typography variant="body2" sx={{ fontWeight: 700 }}>
                  Downline streak rewards
                </Typography>
                <Typography variant="body2" sx={{ fontWeight: 800 }}>
                  {Number(diamondBreakdownTotals.downlineSum).toLocaleString()} 💎
                </Typography>
              </Box>
              <Divider sx={{ my: 2 }} />
              <Box
                sx={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                  gap: 2,
                  py: 0.75,
                }}
              >
                <Typography variant="body2" color="text.secondary" sx={{ fontWeight: 600 }}>
                  Total diamonds
                </Typography>
                <Typography variant="body2" sx={{ fontWeight: 800 }}>
                  {Number(diamondBreakdownTotals.totalSum).toLocaleString()} 💎
                </Typography>
              </Box>
              <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1.5, lineHeight: 1.5 }}>
                Per-app rows are base/sync (AppCoins). Rank and downline are claimed rewards. Total diamonds = sum of
                everything above.
              </Typography>
            </>
          )}
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={() => setDiamondBreakdownOpen(false)} variant="contained" color="primary">
            Close
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default WelcomeHeader;
